export type OfflineSaleStatus = "pending" | "syncing" | "needs_attention";

export type OfflineSaleReceipt = {
  receiptNumber: string;
  total: number;
  isCredit: boolean;
  balance: number;
  paymentMethod: string;
  discount: number;
  tax: number;
  createdAt: string;
  staffName?: string;
  customerName?: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
  items: Array<{
    productName: string;
    quantity: number;
    unitPrice: number;
    total: number;
    unitMode?: string;
  }>;
};

export type OfflineSale = {
  clientReference: string;
  storeId: number;
  userId: number;
  body: Record<string, unknown>;
  receipt: OfflineSaleReceipt;
  status: OfflineSaleStatus;
  createdAt: string;
  lastAttemptAt?: string;
  error?: string;
};

export type OfflineSaleSummary = {
  pending: number;
  syncing: number;
  needsAttention: number;
  total: number;
};

export const OFFLINE_SALES_EVENT = "retailpulse:offline-sales-updated";
export const OFFLINE_SALES_ERROR_EVENT = "retailpulse:offline-sales-error";
export const OFFLINE_SALES_QUEUE_ERROR_MESSAGE =
  "The saved-sale queue could not be checked or updated. Do not re-enter a possibly saved sale; reconnect and try again or ask a manager for help.";

const DB_NAME = "retailpulse-pos";
const DB_VERSION = 1;
const STORE_NAME = "offline-sales";
let activeSync: Promise<OfflineSaleSummary> | null = null;

export function reportOfflineSalesError(): void {
  window.dispatchEvent(new Event(OFFLINE_SALES_ERROR_EVENT));
}

export function shouldShowOfflineSalesStatus(
  isOnline: boolean,
  summary: OfflineSaleSummary,
  hasQueueError = false,
): boolean {
  return !isOnline || summary.total > 0 || hasQueueError;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: "clientReference" });
        store.createIndex("scope", ["storeId", "userId"], { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Could not open offline sales storage"));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore, setResult: (value: T) => void, reject: (reason?: unknown) => void) => void,
): Promise<T> {
  const database = await openDatabase();
  return new Promise<T>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    let result: T;
    let hasResult = false;
    let settled = false;
    const rejectOnce = (reason?: unknown) => {
      if (settled) return;
      settled = true;
      database.close();
      reject(reason);
    };
    transaction.oncomplete = () => {
      database.close();
      if (settled) return;
      settled = true;
      if (!hasResult) {
        reject(new Error("Offline sales transaction completed without a result"));
        return;
      }
      resolve(result);
    };
    transaction.onerror = () => {
      rejectOnce(transaction.error ?? new Error("Offline sales storage failed"));
    };
    transaction.onabort = () => {
      rejectOnce(transaction.error ?? new Error("Offline sales transaction was aborted"));
    };
    try {
      operation(
        store,
        value => {
          result = value;
          hasResult = true;
        },
        rejectOnce,
      );
    } catch (error) {
      rejectOnce(error);
      try {
        transaction.abort();
      } catch {
        // The transaction may already have completed or aborted.
      }
    }
  });
}

function emitUpdate() {
  window.dispatchEvent(new CustomEvent(OFFLINE_SALES_EVENT));
}

export function createSaleClientReference(): string {
  return `pos-${globalThis.crypto.randomUUID()}`;
}

export async function queueOfflineSale(sale: OfflineSale): Promise<void> {
  await withStore<void>("readwrite", (store, setResult, reject) => {
    const request = store.put(sale);
    request.onsuccess = () => setResult();
    request.onerror = () => reject(request.error ?? new Error("Could not save the offline sale"));
  });
  emitUpdate();
}

export async function listOfflineSales(storeId: number, userId: number): Promise<OfflineSale[]> {
  return withStore<OfflineSale[]>("readonly", (store, setResult, reject) => {
    const request = store.index("scope").getAll(IDBKeyRange.only([storeId, userId]));
    request.onsuccess = () => {
      const rows = (request.result as OfflineSale[])
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      setResult(rows);
    };
    request.onerror = () => reject(request.error ?? new Error("Could not read queued offline sales"));
  });
}

async function updateOfflineSale(sale: OfflineSale): Promise<void> {
  await withStore<void>("readwrite", (store, setResult, reject) => {
    const request = store.put(sale);
    request.onsuccess = () => setResult();
    request.onerror = () => reject(request.error ?? new Error("Could not update the queued offline sale"));
  });
}

async function deleteOfflineSale(clientReference: string): Promise<void> {
  await withStore<void>("readwrite", (store, setResult, reject) => {
    const request = store.delete(clientReference);
    request.onsuccess = () => setResult();
    request.onerror = () => reject(request.error ?? new Error("Could not remove the synchronized offline sale"));
  });
}

export async function retryOfflineSale(clientReference: string): Promise<void> {
  await withStore<void>("readwrite", (store, setResult, reject) => {
    const getRequest = store.get(clientReference);
    getRequest.onsuccess = () => {
      const sale = getRequest.result as OfflineSale | undefined;
      if (!sale) {
        setResult();
        return;
      }
      const putRequest = store.put({ ...sale, status: "pending", error: undefined });
      putRequest.onsuccess = () => setResult();
      putRequest.onerror = () => reject(putRequest.error ?? new Error("Could not mark the offline sale for retry"));
    };
    getRequest.onerror = () => reject(getRequest.error ?? new Error("Could not load the offline sale for retry"));
  });
  emitUpdate();
}

export function summarizeOfflineSales(sales: OfflineSale[]): OfflineSaleSummary {
  const pending = sales.filter(sale => sale.status === "pending").length;
  const syncing = sales.filter(sale => sale.status === "syncing").length;
  const needsAttention = sales.filter(sale => sale.status === "needs_attention").length;
  return { pending, syncing, needsAttention, total: sales.length };
}

export async function syncOfflineSales(
  storeId: number,
  userId: number,
  basePath: string,
): Promise<OfflineSaleSummary> {
  if (activeSync) return activeSync;
  activeSync = (async () => {
    const queued = await listOfflineSales(storeId, userId);
    for (const sale of queued) {
      if (sale.status === "needs_attention") continue;
      const syncingSale = {
        ...sale,
        status: "syncing" as const,
        lastAttemptAt: new Date().toISOString(),
        error: undefined,
      };
      await updateOfflineSale(syncingSale);
      emitUpdate();

      try {
        const response = await fetch(`${basePath}/api/sales`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Idempotency-Key": sale.clientReference,
          },
          credentials: "include",
          body: JSON.stringify(sale.body),
        });
        if (response.ok) {
          await deleteOfflineSale(sale.clientReference);
          emitUpdate();
          continue;
        }

        let message = "This sale could not be synchronized.";
        try {
          const payload = await response.json();
          if (typeof payload?.error === "string") message = payload.error;
        } catch {
          // Keep the generic message when the server does not return JSON.
        }
        if (response.status === 401 || response.status >= 500) {
          await updateOfflineSale({ ...syncingSale, status: "pending", error: message });
          emitUpdate();
          break;
        }
        await updateOfflineSale({ ...syncingSale, status: "needs_attention", error: message });
        emitUpdate();
      } catch {
        await updateOfflineSale({
          ...syncingSale,
          status: "pending",
          error: "Still offline. This sale will retry when the connection returns.",
        });
        emitUpdate();
        break;
      }
    }
    return summarizeOfflineSales(await listOfflineSales(storeId, userId));
  })().finally(() => {
    activeSync = null;
  });
  return activeSync;
}
